import { useEffect, useMemo, useRef } from "react";
import { useOfficialMatchHeader } from "@/hooks/useOfficialMatchHeader";
import { useLanguage } from "@/hooks/useLanguage";
import { useMounted } from "@/hooks/useMounted";
import { trackEvent } from "@/lib/audit";

interface Props {
  fixtureId: string;
  /** Fallback labels only used while the provider payload is loading. */
  fallbackHomeCode?: string;
  fallbackAwayCode?: string;
  fallbackHomeFlag?: string;
  fallbackAwayFlag?: string;
  className?: string;
}

/**
 * Real Data Phase 5 — Official Match Header.
 *
 * Renders ONLY official API-Football values. Every displayed field
 * (score, minute, status, added time, penalties, kickoff, HT/FT
 * indicators) comes from `useOfficialMatchHeader`. The clock is never
 * generated locally: the provider elapsed minute always wins. If the
 * provider is unavailable, a lifecycle-appropriate placeholder is shown
 * — no fabricated data ever reaches the header.
 */
export function OfficialMatchHeader({
  fixtureId,
  fallbackHomeCode,
  fallbackAwayCode,
  fallbackHomeFlag,
  fallbackAwayFlag,
  className,
}: Props) {
  const { lang } = useLanguage();
  const mounted = useMounted();
  const { details, status, isOfficial, isLoading, isError } =
    useOfficialMatchHeader(fixtureId);

  const trackedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${fixtureId}:${status.short ?? "?"}:${isOfficial ? "1" : "0"}`;
    if (trackedRef.current === key) return;
    trackedRef.current = key;
    void trackEvent("official_match_header_state", {
      fixtureId,
      lifecycle: status.lifecycle,
      phase: status.phase,
      short: status.short,
      official: isOfficial,
    });
  }, [fixtureId, status, isOfficial]);

  const kickoffText = useMemo(() => {
    if (!details?.kickoff) return null;
    try {
      const d = new Date(details.kickoff);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString(
        lang === "EN" ? "en-US" : lang === "PT" ? "pt-BR" : "es-ES",
        {
          weekday: "short",
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        },
      );
    } catch {
      return null;
    }
  }, [details?.kickoff, lang]);

  const homeName =
    details?.homeTeam ?? fallbackHomeCode ?? "";
  const awayName =
    details?.awayTeam ?? fallbackAwayCode ?? "";
  const homeLogo = details?.homeLogo ?? null;
  const awayLogo = details?.awayLogo ?? null;

  const scoreHome = details?.scoreHome;
  const scoreAway = details?.scoreAway;
  const penHome = details?.penaltyHome;
  const penAway = details?.penaltyAway;

  // Loading (no data yet).
  if (!mounted || isLoading) {
    return (
      <div
        className={
          "flex items-center gap-2 rounded-2xl glass px-2 py-1 sm:px-4 sm:py-1.5 " +
          (className ?? "")
        }
        aria-busy="true"
      >
        <span className="text-[10px] font-black tracking-wider text-muted-foreground">
          {lang === "EN" ? "Loading…" : lang === "PT" ? "Carregando…" : "Cargando…"}
        </span>
      </div>
    );
  }

  // Provider error / not official — never fabricate.
  if (isError || !isOfficial || !details) {
    return (
      <div
        className={
          "flex items-center gap-2 rounded-2xl glass px-2 py-1 sm:px-4 sm:py-1.5 " +
          (className ?? "")
        }
        role="status"
        aria-live="polite"
      >
        <span className="text-[10px] font-black tracking-wider text-muted-foreground">
          {lang === "EN"
            ? "Match unavailable"
            : lang === "PT"
            ? "Partida indisponível"
            : "Partido no disponible"}
        </span>
      </div>
    );
  }

  // Pill colour / label per lifecycle.
  let pillClasses = "bg-muted text-muted-foreground";
  if (status.isLive) pillClasses = "bg-primary text-primary-foreground";
  else if (status.isBreak) pillClasses = "bg-[oklch(0.78_0.18_85)] text-[oklch(0.18_0.02_80)]";
  else if (status.isFinished) pillClasses = "bg-foreground/80 text-background";

  // Clock display — provider authoritative.
  let clockDisplay: string;
  if (status.lifecycle === "SCHEDULED") {
    clockDisplay = kickoffText ?? (lang === "EN" ? "Kick-off" : lang === "PT" ? "Início" : "Inicio");
  } else if (status.phase === "halftime") {
    clockDisplay = "HT";
  } else if (status.phase === "break_time") {
    clockDisplay = "BT";
  } else if (status.phase === "full_time") {
    clockDisplay = status.pillLabel; // FT / AET / PEN
  } else if (status.phase === "penalties" && status.isLive) {
    clockDisplay = "PEN";
  } else if (status.phase === "extra_time") {
    const m = typeof details.minute === "number" ? details.minute : 90;
    const extra = details.extraMinute ? `+${details.extraMinute}` : "";
    clockDisplay = `ET ${m}${extra}'`;
  } else if (status.isLive && typeof details.minute === "number") {
    const extra = details.extraMinute ? `+${details.extraMinute}` : "";
    clockDisplay = `${details.minute}${extra}'`;
  } else if (status.isSuspended) {
    clockDisplay = "SUSP";
  } else if (status.isCancelled) {
    clockDisplay = status.pillLabel;
  } else {
    clockDisplay = status.pillLabel;
  }

  const showRegScore =
    typeof scoreHome === "number" && typeof scoreAway === "number";
  const showPenScore =
    typeof penHome === "number" && typeof penAway === "number";

  const homeInitial = fallbackHomeFlag ?? "";
  const awayInitial = fallbackAwayFlag ?? "";

  return (
    <div
      className={
        "flex shrink-0 items-center gap-1.5 sm:gap-3 rounded-2xl glass px-2 sm:px-4 py-1 sm:py-1.5 " +
        (className ?? "")
      }
      aria-label={`${homeName} ${scoreHome ?? "-"} - ${scoreAway ?? "-"} ${awayName} · ${clockDisplay}`}
    >
      <div className={`flex items-center gap-1.5 rounded-full px-1.5 py-0.5 ${pillClasses}`}>
        {status.isLive && !status.isBreak && (
          <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse-dot" />
        )}
        <span className="text-[10px] font-black tracking-wider">
          {status.pillLabel}
        </span>
      </div>
      <div className="hidden sm:block h-5 w-px bg-border" />
      <div className="flex items-center gap-1.5 sm:gap-3 text-sm sm:text-base font-bold tabular-nums">
        <span className="flex items-center gap-1">
          {homeLogo ? (
            <img src={homeLogo} alt="" className="h-4 w-4 object-contain" />
          ) : (
            <span aria-hidden>{homeInitial}</span>
          )}
          <span className="hidden sm:inline">{homeName.slice(0, 3).toUpperCase()}</span>
        </span>
        <span className="text-base sm:text-2xl font-black tabular-nums tracking-tighter">
          {showRegScore ? (
            <>
              {scoreHome} <span className="text-muted-foreground/60">-</span> {scoreAway}
            </>
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </span>
        <span className="flex items-center gap-1">
          <span className="hidden sm:inline">{awayName.slice(0, 3).toUpperCase()}</span>
          {awayLogo ? (
            <img src={awayLogo} alt="" className="h-4 w-4 object-contain" />
          ) : (
            <span aria-hidden>{awayInitial}</span>
          )}
        </span>
      </div>
      {showPenScore && (
        <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-black tracking-wider text-primary">
          PEN {penHome}-{penAway}
        </span>
      )}
      <div className="hidden sm:block h-5 w-px bg-border" />
      <span className="tabular-nums rounded-md border border-border bg-[oklch(0.08_0.005_260)] px-2 py-0.5 text-[12px] font-black leading-none tracking-normal text-[oklch(0.99_0_0)]">
        {clockDisplay}
      </span>
    </div>
  );
}